import { useCallback, useMemo } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { PageTitle } from '@components/PageTitle';
import { Accordion } from '@components/ui/accordion';
import { EQueryKeys } from '@enums/queryKeys.enum';
import { dateFormatter } from '@formatters/date.formatter';
import { treatmentSearchSchema } from '@schemas/treatmentSearch.schema';
import { treatmentCreatesService } from '@services/treatmentCreates/service';
import { treatmentUpdatesService } from '@services/treatmentUpdates/service';

import { MyTreatmentsItem } from './@components/Item';
import { MyTreatmentsSearchBar } from './@components/SearchBar';

const myTreatmentUpdatesQueryOptions = ({
  page,
  title,
}: {
  page: number;
  title?: string;
}) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.MyTreatmentUpdates, title, page],
    queryFn: () =>
      treatmentUpdatesService.getAllByCollaborator({
        page: page,
        title,
      }),
  });

const myTreatmentCreatesQueryOptions = ({
  page,
  title,
}: {
  page: number;
  title?: string;
}) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.MyTreatmentCreates, title, page],
    queryFn: () =>
      treatmentCreatesService.getAllByCollaborator({
        page: page,
        title,
      }),
  });

export const Route = createFileRoute('/_layout/(private)/my-treatments/')({
  component: MyTreatments,
  validateSearch: search => treatmentSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    page: search.page,
    title: search.title || undefined,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      myTreatmentUpdatesQueryOptions({ title: deps.title, page: deps.page }),
    );
    await context.queryClient.ensureQueryData(
      myTreatmentCreatesQueryOptions({ title: deps.title, page: deps.page }),
    );
  },
});

function MyTreatments() {
  const navigate = Route.useNavigate();
  const { page, title } = Route.useSearch();
  const { data: treatmentUpdates } = useQuery(
    myTreatmentUpdatesQueryOptions({ title, page }),
  );
  const { data: treatmentCreates } = useQuery(
    myTreatmentCreatesQueryOptions({ title, page }),
  );

  const handleSearch = useCallback(
    (value: string) => {
      navigate({
        search: prev => ({
          ...prev,
          title: value || undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const reviewList = useMemo(() => {
    return (
      treatmentUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          treatmentCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'EM_REVISAO') || []
    );
  }, [treatmentCreates?.list, treatmentUpdates?.list]);

  const finishList = useMemo(() => {
    return (
      treatmentUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          treatmentCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'CONCLUIDO') || []
    );
  }, [treatmentCreates?.list, treatmentUpdates?.list]);

  const correctionList = useMemo(() => {
    return (
      treatmentUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          treatmentCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'EM_CORRECAO') || []
    );
  }, [treatmentCreates?.list, treatmentUpdates?.list]);

  return (
    <div>
      <PageTitle title="Meus protocolos" />

      <MyTreatmentsSearchBar onSubmit={handleSearch} />

      <Accordion.Root className="grid gap-4" type="multiple">
        <MyTreatmentsItem
          status="Em revisão"
          treatments={treatmentUpdates?.list
            .filter(item => item.status === 'EM_PRODUCAO')
            .map(item => ({
              status: 'Em revisão',
              id: item.id,
              title: item.title,
              gpCoins: item.amount,
              startedAt: dateFormatter(item.startedAt),
              expiredAt: dateFormatter(item.expiredAt),
              type: 'UPDATE',
            }))}
        />
        <MyTreatmentsItem
          status="Em criação"
          treatments={treatmentCreates?.list
            .filter(item => item.status === 'EM_PRODUCAO')
            .map(item => ({
              status: 'Em criação',
              id: item.id,
              title: item.title,
              gpCoins: item.amount,
              startedAt: dateFormatter(item.startedAt),
              expiredAt: dateFormatter(item.expiredAt),
              type: 'CREATE',
            }))}
        />
        <MyTreatmentsItem
          status="Em aprovação"
          treatments={reviewList.map(item => ({
            status: 'Em aprovação',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: null,
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
        <MyTreatmentsItem
          status="Em correção"
          treatments={correctionList.map(item => ({
            status: 'Em correção',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: dateFormatter(item.expiredAt),
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
        <MyTreatmentsItem
          status="Concluído"
          treatments={finishList.map(item => ({
            status: 'Concluído',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: null,
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
      </Accordion.Root>
    </div>
  );
}
